import html2canvas from 'html2canvas';
import React from 'react';
import { createRoot } from 'react-dom/client';

const DEFAULT_RENDER_MS = 2400;
/** Inner chart column width; host adds horizontal padding so PNG is balanced and uncropped */
const INNER_WIDTH = 940;
const HORIZONTAL_GUTTER = 48;

const sanitizeStem = (s) =>
  String(s || 'chart')
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

/**
 * Render a print-style React chart off-screen and download as PNG (white background).
 * @param {Object} options
 * @param {React.ComponentType} options.Component - Print* chart component
 * @param {Object} options.props - Props passed to Component
 * @param {string} options.filename - Download filename (should end with .png)
 * @param {number} [options.renderWaitMs]
 * @param {number} [options.scale]
 */
export async function downloadPrintChartAsPng({
  Component,
  props,
  filename,
  renderWaitMs = DEFAULT_RENDER_MS,
  scale = 2
}) {
  if (!Component) throw new Error('chartImageExport: Component is required');

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  const outerWidth = INNER_WIDTH + 2 * HORIZONTAL_GUTTER;
  host.style.cssText = [
    'position:fixed',
    'left:-12000px',
    'top:0',
    `width:${outerWidth}px`,
    `padding:0 ${HORIZONTAL_GUTTER}px`,
    'box-sizing:border-box',
    'background:#ffffff',
    'overflow:visible',
    'pointer-events:none',
    'z-index:-1'
  ].join(';');
  document.body.appendChild(host);

  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    `width:${INNER_WIDTH}px`,
    'max-width:100%',
    'margin:0 auto',
    'padding:8px 0 2px',
    'box-sizing:border-box',
    'background:#ffffff'
  ].join(';');
  host.appendChild(wrapper);

  const root = createRoot(wrapper);
  root.render(React.createElement(Component, props));

  await new Promise((r) => setTimeout(r, renderWaitMs));

  const canvas = await html2canvas(host, {
    backgroundColor: '#ffffff',
    scale,
    logging: false,
    useCORS: true,
    allowTaint: true,
    width: outerWidth,
    windowWidth: outerWidth
  });

  const name = filename.endsWith('.png') ? filename : `${filename}.png`;
  const link = document.createElement('a');
  link.href = canvas.toDataURL('image/png');
  link.download = name;
  link.click();

  root.unmount();
  document.body.removeChild(host);
}

/**
 * @param {Array<{ Component: React.ComponentType, props: Object, filenameStem: string }>} jobs
 * @param {Object} [opts]
 * @param {number} [opts.pauseMs] - Delay between files (multiple exports)
 */
export async function downloadPrintChartsJobList(jobs, { pauseMs = 450, renderWaitMs, scale } = {}) {
  if (!jobs?.length) return;

  const date = new Date().toISOString().slice(0, 10);

  for (let i = 0; i < jobs.length; i += 1) {
    const { Component, props, filenameStem } = jobs[i];
    const stem = sanitizeStem(filenameStem);
    const suffix = jobs.length > 1 ? `_${i + 1}` : '';
    await downloadPrintChartAsPng({
      Component,
      props,
      filename: `${stem}_${date}${suffix}.png`,
      renderWaitMs,
      scale
    });
    if (i < jobs.length - 1) {
      await new Promise((r) => setTimeout(r, pauseMs));
    }
  }
}
